import { useRef } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";

/**
 * ダイアログの「外側を押して閉じる」を、**全プロダクトで同じ規則**にする（PRODUCT_DEV-710）。
 *
 * ## 何が起きていたか
 *
 * オーバーレイ（`fixed inset-0`）に `onClick={onClose}` を付け、中のパネルに
 * `onClick={(e) => e.stopPropagation()}` を付ける書き方が、dev-lab / admin / unizo に
 * 計 7 箇所あった。この形は**ダイアログの中で文字をドラッグ選択して外側で指を離すと閉じる**。
 *
 * `click` は mousedown と mouseup の**共通祖先**で発火する。中で押して外で離すと、
 * その祖先はオーバーレイ自身になり、click の target はパネルの子孫ではないので
 * パネルの `stopPropagation` は**一度も呼ばれない**。そのままオーバーレイの
 * `onClick` が走って閉じる。依頼文を選んでコピーしようとするたびに閉じていた。
 *
 * ## この規則
 *
 * **押した位置と離した位置の両方がパネルの外だったときだけ閉じる。**
 *
 * | 操作 | 結果 |
 * |---|---|
 * | 中で押して外で離す（選択のドラッグ） | 閉じない |
 * | 外で押して中で離す | 閉じない |
 * | 外で押して外で離す（本当の外側クリック） | 閉じる |
 *
 * ## 「外」の決め方
 *
 * **`e.target === e.currentTarget` では判定しない。** オーバーレイとパネルの間に
 * 中央寄せ用のラッパを挟む実装があり（admin の `Modal.tsx` は
 * `overflow-y-auto` なオーバーレイ + `min-h-full flex items-center` なラッパ）、
 * そのラッパが**背景の可視領域をほぼ覆う**。同一判定だと背景を押しても target が
 * ラッパになり、**外側クリックが一切効かなくなる**。
 *
 * そこで**パネルに印を付けて、その中かどうかで見る**。パネル側に
 * `{...OVERLAY_PANEL_PROPS}` を広げること。印が無いと「常に外側」と見なされ、
 * パネルの中を押しただけで閉じる。
 *
 * ## 使い方
 *
 * ```tsx
 * const overlay = useOverlayDismiss(dismissible ? onClose : undefined);
 * return (
 *   <div className="fixed inset-0 ..." {...overlay}>
 *     <div className="..." {...OVERLAY_PANEL_PROPS}>{children}</div>
 *   </div>
 * );
 * ```
 *
 * パネル側の `onClick={(e) => e.stopPropagation()}` は**外してよい**（この規則が代わりに
 * 効く）。残しても害は無い。
 *
 * **背景をパネルの兄弟として置く実装（`<div className="absolute inset-0" onClick=... />`）
 * には要らない。** その形では click の target が背景にならないので、もともと閉じない。
 */

/** パネルであることの印。**パネル要素に広げる。** */
export const OVERLAY_PANEL_PROPS = { "data-overlay-panel": "" } as const;

const PANEL_SELECTOR = "[data-overlay-panel]";

/**
 * その位置がパネルの外か。
 *
 * - `closest` を持たないもの（テキストノード等）は**外とみなさない**。判定できないものを
 *   根拠に閉じない、に倒す（誤って閉じるほうが実害が大きい）
 * - オーバーレイの外で起きたものも外とみなさない（別ツリーのクリックで閉じない）
 */
export const isOutsideOverlayPanel = (
  target: unknown,
  currentTarget: unknown,
): boolean => {
  const el = target as { closest?: (s: string) => unknown } | null;
  if (!el || typeof el.closest !== "function") return false;
  const root = currentTarget as { contains?: (n: unknown) => boolean } | null;
  if (root && typeof root.contains === "function" && !root.contains(el)) {
    return false;
  }
  return el.closest(PANEL_SELECTOR) === null;
};

/**
 * 閉じてよいか。**両方が真のときだけ**。
 *
 * 純関数にしてあるのは、片方を落とす「単純化」を機械で止めるため
 * （この不具合はまさに片方しか見ていなかったことが原因だった）。
 */
export const shouldDismissOnOverlay = (
  pressedOutside: boolean,
  releasedOutside: boolean,
): boolean => pressedOutside && releasedOutside;

export interface OverlayDismissHandlers {
  onMouseDown: (e: ReactMouseEvent) => void;
  onMouseUp: (e: ReactMouseEvent) => void;
  onClick: (e: ReactMouseEvent) => void;
}

/**
 * @param onDismiss 閉じる処理。**閉じさせたくないときは undefined を渡す**
 *   （フックの呼び出し自体は必ず行うこと。条件付きで呼ぶと hook の数が変わる）。
 */
export const useOverlayDismiss = (
  onDismiss?: (() => void) | undefined,
): OverlayDismissHandlers => {
  const pressedOutside = useRef(false);
  const releasedOutside = useRef(false);

  return {
    onMouseDown: (e) => {
      pressedOutside.current = isOutsideOverlayPanel(e.target, e.currentTarget);
    },
    onMouseUp: (e) => {
      releasedOutside.current = isOutsideOverlayPanel(
        e.target,
        e.currentTarget,
      );
    },
    onClick: () => {
      if (!onDismiss) return;
      if (
        !shouldDismissOnOverlay(pressedOutside.current, releasedOutside.current)
      ) {
        return;
      }
      // 次の開閉へ持ち越さない（同じ要素が使い回されることがある）。
      pressedOutside.current = false;
      releasedOutside.current = false;
      onDismiss();
    },
  };
};
